/**
 * PDV (Point of Sale) Main Page
 * Full-screen interface for cashier operations.
 * Layout: Header, Main Area (barcode + items), Side Panel (summary), Action Buttons Bar.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Trash2,
  Minus,
  Plus,
  ShoppingCart,
  User,
  Bell,
  Wifi,
  WifiOff,
  LockKeyhole,
  LogOut,
  CheckCircle,
  CreditCard,
  Percent,
  XCircle,
  Ban,
  ArrowDownUp,
  UserCheck,
  Package,
  Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { apiFetch } from '@/lib/api';
import { beepSuccess, beepError, beepPayment } from '@/lib/sounds';
import { printReceipt } from '@/lib/printer';
import { usePdvState } from './hooks/usePdvState';
import type { SaleItem } from './hooks/usePdvState';
import { useBarcodeScan } from './hooks/useBarcodeScan';
import type { SearchProduct } from './hooks/useBarcodeScan';
import { usePdvKeyboard } from './hooks/usePdvKeyboard';
import { QuickProductModal } from './components/QuickProductModal';
import type { QuickProductData } from './components/QuickProductModal';
import { WeightInputModal } from './components/WeightInputModal';
import { PaymentModal } from './components/PaymentModal';
import { DiscountModal } from './components/DiscountModal';
import { CancelSaleModal } from './components/CancelSaleModal';
import { RefundModal } from './components/RefundModal';
import { CashRegisterModal } from './components/CashRegisterModal';
import { SangriaModal } from './components/SangriaModal';
import { CustomerSearchModal } from './components/CustomerSearchModal';

export function PdvPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const {
    state,
    addItem,
    updateItemQuantity,
    removeItem,
    setDiscount,
    setCustomer,
    clearSale,
    loadExistingSale,
  } = usePdvState();

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [quickProductModal, setQuickProductModal] = useState<{ open: boolean; barcode: string }>({
    open: false,
    barcode: '',
  });
  const [weightModal, setWeightModal] = useState<{
    open: boolean;
    productName: string;
    unitPrice: number;
    unit: string;
    productId: string;
  }>({ open: false, productName: '', unitPrice: 0, unit: 'kg', productId: '' });

  const [paymentModal, setPaymentModal] = useState(false);
  const [discountModal, setDiscountModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [cashRegisterModal, setCashRegisterModal] = useState<{ open: boolean; mode: 'open' | 'close' }>({
    open: false,
    mode: 'open',
  });
  const [sangriaModal, setSangriaModal] = useState(false);
  const [customerSearchModal, setCustomerSearchModal] = useState(false);
  const [registerOpen, setRegisterOpen] = useState<boolean | null>(null);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [customerPoints, setCustomerPoints] = useState<number | null>(null);
  const [lastReceipt, setLastReceipt] = useState<string | null>(() => localStorage.getItem('nexopdv_last_receipt'));

  const itemListRef = useRef<HTMLDivElement>(null);

  // Clock update
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Conexão restaurada');
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Operando em modo offline');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check if cash register is open
  const checkRegisterStatus = useCallback(async () => {
    try {
      const response = await apiFetch('/api/cash-register/current');
      if (response.ok) {
        setRegisterOpen(true);
      } else {
        setRegisterOpen(false);
      }
    } catch {
      // If offline, assume register is open (offline mode)
      setRegisterOpen(true);
    }
  }, []);

  useEffect(() => {
    checkRegisterStatus();
  }, [checkRegisterStatus]);

  // Load existing open sale on mount (resume interrupted sale)
  useEffect(() => {
    loadExistingSale();
  }, [loadExistingSale]);

  // Fetch loyalty points when customer is set
  useEffect(() => {
    if (state.customer?.id) {
      apiFetch(`/api/loyalty/customer/${state.customer.id}`)
        .then((res) => {
          if (res.ok) return res.json();
          return null;
        })
        .then((data) => {
          if (data) {
            setCustomerPoints(data.pontos_disponiveis ?? 0);
          } else {
            setCustomerPoints(null);
          }
        })
        .catch(() => setCustomerPoints(null));
    } else {
      setCustomerPoints(null);
    }
  }, [state.customer?.id]);

  // Handle barcode scan - lookup product
  const handleBarcodeScan = useCallback(
    async (barcode: string) => {
      try {
        const response = await apiFetch(`/api/products/barcode/${barcode}`);

        if (response.status === 404) {
          // Product not found - offer quick registration
          beepError();
          setQuickProductModal({ open: true, barcode });
          return;
        }

        if (!response.ok) {
          toast.error('Erro ao buscar produto');
          return;
        }

        const product = await response.json();

        // Stock warnings (ITEM 13)
        if (product.status === 'esgotado' || product.stock_quantity <= 0) {
          toast.warning('ATENÇÃO: Produto esgotado no estoque!');
        } else if (product.status === 'baixo') {
          toast.warning(`Estoque baixo: ${product.stock_quantity} unidades restantes`);
        }

        // Check if product is sold by weight/volume
        if (product.unit === 'kg' || product.unit === 'litro' || product.unit === 'metro') {
          setWeightModal({
            open: true,
            productName: product.name,
            unitPrice: product.has_promotion ? Number(product.promotional_price) : Number(product.sale_price),
            unit: product.unit,
            productId: product.id,
          });
          return;
        }

        // Use promotional price if available
        const effectivePrice = product.has_promotion ? Number(product.promotional_price) : Number(product.sale_price);

        // Add product with quantity 1
        const newItem: SaleItem = {
          id: `${product.id}-${Date.now()}`,
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: effectivePrice,
          unit: product.unit,
          subtotal: effectivePrice,
          original_price: product.has_promotion ? Number(product.sale_price) : undefined,
          has_promotion: product.has_promotion || false,
        };

        addItem(newItem);
        if (product.has_promotion) {
          toast.success(`${product.name} adicionado (PROMO -${product.promotion_discount_pct}%)`);
        } else {
          toast.success(`${product.name} adicionado`);
        }
        beepSuccess();

        // Scroll to bottom of item list
        setTimeout(() => {
          itemListRef.current?.scrollTo({ top: itemListRef.current.scrollHeight, behavior: 'smooth' });
        }, 100);
      } catch {
        toast.error('Erro de conexão ao buscar produto');
      }
    },
    [addItem],
  );

  const handleInvalidCode = useCallback((code: string) => {
    beepError();
    toast.error(`Código inválido: "${code}". Use apenas números (4-13 dígitos).`);
  }, []);

  // Handle product selection from search dropdown
  const handleProductSelect = useCallback(
    (product: SearchProduct) => {
      // Check if product is sold by weight/volume
      if (product.unit === 'kg' || product.unit === 'litro' || product.unit === 'metro') {
        setWeightModal({
          open: true,
          productName: product.name,
          unitPrice: Number(product.sale_price),
          unit: product.unit,
          productId: product.id,
        });
        return;
      }

      // Add product with quantity 1
      const newItem: SaleItem = {
        id: `${product.id}-${Date.now()}`,
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: Number(product.sale_price),
        unit: product.unit,
        subtotal: Number(product.sale_price),
      };

      addItem(newItem);
      toast.success(`${product.name} adicionado`);
      beepSuccess();

      setTimeout(() => {
        itemListRef.current?.scrollTo({ top: itemListRef.current.scrollHeight, behavior: 'smooth' });
      }, 100);
    },
    [addItem],
  );

  const { inputRef, handleKeyDown: handleBarcodeKeyDown, handleInputChange, focusInput, searchResults, isSearching, showDropdown, selectedIndex, selectProduct, closeDropdown } = useBarcodeScan({
    onScan: handleBarcodeScan,
    onInvalidCode: handleInvalidCode,
    onProductSelect: handleProductSelect,
  });

  // Auto-focus barcode input
  useEffect(() => {
    focusInput();
  }, [focusInput]);

  // Re-focus after modal closes
  useEffect(() => {
    if (!quickProductModal.open && !weightModal.open && !paymentModal && !discountModal && !cancelModal && !refundModal && !cashRegisterModal.open && !sangriaModal) {
      setTimeout(focusInput, 300);
    }
  }, [quickProductModal.open, weightModal.open, paymentModal, discountModal, cancelModal, refundModal, cashRegisterModal.open, sangriaModal, focusInput]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  // Handle quick product save
  const handleQuickProductSave = useCallback(
    async (productData: QuickProductData) => {
      try {
        const response = await apiFetch('/api/products', {
          method: 'POST',
          body: JSON.stringify({
            barcode: productData.barcode,
            name: productData.name,
            sale_price: productData.sale_price,
            cost_price: 0,
            unit: productData.unit,
            category: productData.category,
            stock_quantity: 0,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          toast.error(err.error || 'Erro ao cadastrar produto');
          return;
        }

        const product = await response.json();

        // Check if weight-based product
        if (product.unit === 'kg' || product.unit === 'litro' || product.unit === 'metro') {
          setQuickProductModal({ open: false, barcode: '' });
          setWeightModal({
            open: true,
            productName: product.name,
            unitPrice: Number(product.sale_price),
            unit: product.unit,
            productId: product.id,
          });
          return;
        }

        // Add to sale
        const newItem: SaleItem = {
          id: `${product.id}-${Date.now()}`,
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: Number(product.sale_price),
          unit: product.unit,
          subtotal: Number(product.sale_price),
        };

        addItem(newItem);
        setQuickProductModal({ open: false, barcode: '' });
        toast.success(`${product.name} cadastrado e adicionado`);
        beepSuccess();
      } catch {
        toast.error('Erro ao cadastrar produto');
      }
    },
    [addItem],
  );

  // Handle weight confirmation
  const handleWeightConfirm = useCallback(
    (quantity: number) => {
      const newItem: SaleItem = {
        id: `${weightModal.productId}-${Date.now()}`,
        product_id: weightModal.productId,
        product_name: weightModal.productName,
        quantity,
        unit_price: weightModal.unitPrice,
        unit: weightModal.unit,
        subtotal: Math.round(quantity * weightModal.unitPrice * 100) / 100,
      };

      addItem(newItem);
      setWeightModal({ open: false, productName: '', unitPrice: 0, unit: 'kg', productId: '' });
      toast.success(`${weightModal.productName} adicionado (${quantity.toFixed(3)} ${weightModal.unit})`);
      beepSuccess();
    },
    [addItem, weightModal],
  );

  // Handle quantity change
  const handleQuantityChange = useCallback(
    (itemId: string, delta: number) => {
      const item = state.items.find((i) => i.id === itemId);
      if (!item) return;
      const newQty = item.quantity + delta;
      if (newQty <= 0) {
        removeItem(itemId);
        toast.info('Item removido');
      } else {
        updateItemQuantity(itemId, newQty);
      }
    },
    [state.items, updateItemQuantity, removeItem],
  );

  // Reprint last receipt
  const handleReprintReceipt = useCallback(() => {
    const stored = lastReceipt || localStorage.getItem('nexopdv_last_receipt');
    if (!stored) {
      toast.info('Nenhum cupom anterior disponível');
      return;
    }

    // Check if it's raw HTML (starts with <!DOCTYPE or <html) or JSON receipt data
    if (stored.trim().startsWith('<') || stored.trim().startsWith('<!')) {
      printReceipt(stored);
    } else {
      // It's JSON receipt data - generate HTML
      try {
        const data = JSON.parse(stored);
        const now = new Date();
        const dateStr = now.toLocaleDateString('pt-BR');
        const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const tenantName = localStorage.getItem('nexopdv_tenant_name') || localStorage.getItem('tenant_name') || 'Meu Estabelecimento';

        const paymentLabels: Record<string, string> = {
          dinheiro: 'Dinheiro', cartao_debito: 'Cartão Débito', cartao_credito: 'Cartão Crédito', pix: 'PIX', fiado: 'Crédito',
        };

        const paymentLines = (data.payments || [])
          .map((p: { method: string; amount: number }) => `<tr><td>${paymentLabels[p.method] || p.method}</td><td style="text-align:right">R$ ${p.amount.toFixed(2)}</td></tr>`)
          .join('');

        const itemLines = (data.items || [])
          .map((item: { product_name: string; quantity: number; unit_price: number; subtotal: number; unit: string }, idx: number) => {
            const qtyStr = item.unit !== 'un' ? item.quantity.toFixed(3) : String(item.quantity);
            return `<tr><td colspan="2" style="padding-top:4px"><small>${String(idx + 1).padStart(2, '0')}</small> ${item.product_name}</td></tr><tr><td style="padding-left:20px"><small>${qtyStr} x R$ ${item.unit_price.toFixed(2)}</small></td><td style="text-align:right">R$ ${item.subtotal.toFixed(2)}</td></tr>`;
          })
          .join('');

        const html = `<!DOCTYPE html><html><head><title>Cupom ${data.receipt_number} (Reimpressao)</title><style>body{font-family:'Courier New',monospace;font-size:12px;width:280px;margin:0 auto;padding:10px}.center{text-align:center}.divider{border-top:1px dashed #000;margin:8px 0}table{width:100%;border-collapse:collapse}td{padding:2px 0;font-size:11px}.bold{font-weight:bold}.total{font-size:14px;font-weight:bold}@media print{body{width:auto}}</style></head><body><div class="center"><strong>${tenantName}</strong><br/><strong>CUPOM NAO FISCAL (REIMPRESSAO)</strong><br/><small>${dateStr} ${timeStr}</small></div><div class="divider"></div><table><tr><td>Venda No</td><td style="text-align:right">${data.sale_number}</td></tr><tr><td>Cupom</td><td style="text-align:right">${data.receipt_number}</td></tr></table><div class="divider"></div>${itemLines ? `<div class="center bold">ITENS</div><table>${itemLines}</table><div class="divider"></div>` : ''}<table><tr class="total"><td>TOTAL</td><td style="text-align:right">R$ ${data.total.toFixed(2)}</td></tr></table><div class="divider"></div><div class="center bold">PAGAMENTO</div><table>${paymentLines}</table>${data.change > 0 ? `<table><tr><td>Troco</td><td style="text-align:right">R$ ${data.change.toFixed(2)}</td></tr></table>` : ''}<div class="divider"></div><div class="center"><small>Obrigado pela preferencia!</small></div></body></html>`;

        printReceipt(html);
      } catch {
        toast.error('Erro ao reimprimir cupom');
      }
    }
  }, [lastReceipt]);

  // F9 shortcut for reprint
  useEffect(() => {
    const handleF9 = (e: KeyboardEvent) => {
      if (e.key === 'F9') {
        e.preventDefault();
        handleReprintReceipt();
      }
    };
    window.addEventListener('keydown', handleF9);
    return () => window.removeEventListener('keydown', handleF9);
  }, [handleReprintReceipt]);

  // Keyboard shortcuts
  usePdvKeyboard({
    onSearch: focusInput,
    onFinalize: () => {
      if (state.items.length === 0) {
        toast.warning('Adicione itens antes de finalizar');
        return;
      }
      setPaymentModal(true);
    },
    onPayment: () => {
      if (state.items.length === 0) {
        toast.warning('Adicione itens antes de finalizar');
        return;
      }
      setPaymentModal(true);
    },
    onCancelItem: () => {
      if (state.items.length > 0) {
        const lastItem = state.items[state.items.length - 1]!;
        removeItem(lastItem.id);
        toast.info(`${lastItem.product_name} removido`);
      }
    },
    onCancelSale: () => {
      if (state.items.length > 0) {
        setCancelModal(true);
      } else {
        toast.info('Nenhuma venda em andamento');
      }
    },
    onDiscount: () => {
      if (state.items.length === 0) {
        toast.warning('Adicione itens antes de aplicar desconto');
        return;
      }
      setDiscountModal(true);
    },
    onSangria: () => setSangriaModal(true),
    onCustomer: () => setCustomerSearchModal(true),
    enabled: !quickProductModal.open && !weightModal.open && !paymentModal && !discountModal && !cancelModal && !refundModal && !cashRegisterModal.open && !sangriaModal && !customerSearchModal,
  });

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const timeStr = currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex h-full flex-col" onClick={() => { if (!cancelModal && !refundModal && !paymentModal && !discountModal && !quickProductModal.open && !weightModal.open && !cashRegisterModal.open && !sangriaModal && !customerSearchModal) { focusInput(); } closeDropdown(); }}>
      {/* CASH REGISTER NOT OPEN - Block sales */}
      {registerOpen === false && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/95 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <LockKeyhole className="h-10 w-10 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Caixa Fechado</h2>
              <p className="mt-2 text-muted-foreground">
                É necessário abrir o caixa antes de iniciar as vendas.
              </p>
            </div>
            <button
              onClick={() => setCashRegisterModal({ open: true, mode: 'open' })}
              className="rounded-xl bg-gradient-to-r from-primary to-secondary px-8 py-3 text-base font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-[0_0_20px_hsl(160_84%_39%/0.4)]"
            >
              Abrir Caixa
            </button>
          </div>
        </div>
      )}
      {/* HEADER */}
      <header className="flex h-14 items-center justify-between border-b border-border/30 bg-[#0B1120] px-5">
        {/* Left: Brand + Operator */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 28 28" fill="none" className="h-7 w-7">
              <rect width="28" height="28" rx="7" fill="#1E3A5F"/>
              <path d="M8 7h12v3H11v4h8v3H11v7H8V7z" fill="white"/>
              <path d="M14 17c3 0 5 1.5 6.5 3.5" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="text-base font-bold text-white">FluxoPDV</span>
          </div>
          <div className="h-5 w-px bg-white/10" />
          <span className="text-sm text-[#94A3B8]">
            <span className="font-medium text-white">{user?.name || 'Operador'}</span>
            <span className="mx-2 text-white/20">|</span>
            Caixa #1
          </span>
        </div>

        {/* Center: Time + Status */}
        <div className="flex items-center gap-4">
          <span className="font-mono text-sm font-medium text-white">{timeStr}</span>
          <div className="flex items-center gap-1.5">
            {isOnline ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span className="text-xs text-emerald-400">Online</span>
              </>
            ) : (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                <span className="text-xs text-red-400">Offline</span>
              </>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleReprintReceipt}
            className="rounded-lg p-2 text-[#64748B] transition-colors hover:bg-white/5 hover:text-white"
            title="Reimprimir último cupom (F9)"
          >
            <Printer className="h-4 w-4" />
          </button>
          <button className="rounded-lg p-2 text-[#64748B] transition-colors hover:bg-white/5 hover:text-white" aria-label="Notificações">
            <Bell className="h-4 w-4" />
          </button>
          <button
            onClick={() => setLogoutConfirm(true)}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-[#64748B] transition-colors hover:bg-red-500/10 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-xs font-medium">Sair</span>
          </button>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="flex flex-1 overflow-hidden">
        {/* Offline Banner */}
        {!isOnline && (
          <div className="absolute left-0 right-0 top-14 z-30 bg-amber-500 px-4 py-1.5 text-center text-sm font-bold text-black">
            MODO OFFLINE - Pagamentos PIX indisponiveis
          </div>
        )}
        {/* LEFT: Main Area (70%) */}
        <div className="flex flex-[7] flex-col p-4">
          {/* Barcode Input with Search Dropdown */}
          <div className="relative mb-4">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <Search className="h-5 w-5 text-muted-foreground" />
            </div>
            <input
              ref={inputRef}
              type="text"
              data-barcode-input="true"
              placeholder="Código de barras ou nome do produto..."
              onKeyDown={handleBarcodeKeyDown}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-border bg-card/80 py-3.5 pl-11 pr-4 text-base text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:shadow-[0_0_20px_hsl(160_84%_39%/0.2)]"
              autoComplete="off"
              aria-label="Campo de busca por código de barras ou nome"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}

            {/* Search Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-xl border border-border bg-card shadow-2xl shadow-black/40">
                {searchResults.map((product, index) => (
                  <button
                    key={product.id}
                    onClick={(e) => { e.stopPropagation(); selectProduct(product); }}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                      index === selectedIndex ? 'bg-primary/10 border-l-2 border-l-primary' : ''
                    } ${index < searchResults.length - 1 ? 'border-b border-border/30' : ''}`}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/50">
                      <Package className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{product.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {product.barcode && `Cod: ${product.barcode} - `}
                        Estoque: {product.stock_quantity} {product.unit}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold text-primary">
                      {Number(product.sale_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-hidden rounded-xl border border-border/50 bg-card/50">
            {/* Table Header */}
            <div className="grid grid-cols-[3rem_1fr_7rem_7rem_7rem_3rem] gap-2 border-b border-border/50 bg-muted/30 px-4 py-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              <span>#</span>
              <span>Produto</span>
              <span className="text-center">Qtd</span>
              <span className="text-right">Preço</span>
              <span className="text-right">Subtotal</span>
              <span></span>
            </div>

            {/* Items */}
            <div ref={itemListRef} className="h-full overflow-y-auto pb-12">
              {state.items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
                  <ShoppingCart className="h-12 w-12 opacity-30" />
                  <p className="text-sm">Escaneie um produto para iniciar a venda</p>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {state.items.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20, height: 0 }}
                      animate={{ opacity: 1, x: 0, height: 'auto' }}
                      exit={{ opacity: 0, x: 20, height: 0 }}
                      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                      className="grid grid-cols-[3rem_1fr_7rem_7rem_7rem_3rem] items-center gap-2 border-b border-border/30 px-4 py-3 transition-colors hover:bg-muted/20"
                    >
                      <span className="text-sm text-muted-foreground">{index + 1}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-medium text-foreground">{item.product_name}</span>
                        {item.unit !== 'un' && (
                          <span className="text-sm text-muted-foreground">
                            ({item.unit})
                          </span>
                        )}
                        {item.has_promotion && (
                          <span className="rounded bg-green-500/20 px-1.5 py-0.5 text-xs font-bold uppercase text-green-400">
                            PROMO
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleQuantityChange(item.id, -1); }}
                          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label="Diminuir quantidade"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="min-w-[3rem] text-center text-base font-bold text-foreground">
                          {item.unit !== 'un' ? item.quantity.toFixed(3) : item.quantity}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleQuantityChange(item.id, 1); }}
                          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label="Aumentar quantidade"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <span className="text-right text-sm text-muted-foreground">
                        {item.original_price ? (
                          <span className="flex flex-col items-end">
                            <span className="text-[10px] line-through opacity-60">{formatCurrency(item.original_price)}</span>
                            <span className="text-green-400">{formatCurrency(item.unit_price)}</span>
                          </span>
                        ) : (
                          formatCurrency(item.unit_price)
                        )}
                      </span>
                      <span className="text-right text-sm font-medium text-foreground">
                        {formatCurrency(item.subtotal)}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeItem(item.id); toast.info(`${item.product_name} removido`); }}
                        className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Remover ${item.product_name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Side Panel (30%) */}
        <div className="flex w-80 flex-col border-l border-border/50 bg-card/60 p-4 backdrop-blur-sm">
          {/* Sale Summary */}
          <div className="rounded-xl border border-border/50 bg-card p-5">
            <h3 className="mb-4 text-base font-semibold uppercase tracking-wider text-muted-foreground">
              Resumo da Venda
            </h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-base text-muted-foreground">Subtotal</span>
                <span className="text-base font-medium text-foreground">
                  {formatCurrency(state.subtotal)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-base text-muted-foreground">Desconto</span>
                <span className="text-base font-medium text-destructive">
                  {state.discount > 0 ? `-${formatCurrency(state.discount)}` : '-'}
                </span>
              </div>
              <div className="my-3 border-t border-border/50" />
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-foreground">TOTAL</span>
                <span className="text-3xl font-bold text-primary">
                  {formatCurrency(state.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Finalize Button */}
          <button
            onClick={() => {
              if (state.items.length === 0) {
                toast.warning('Adicione itens antes de finalizar');
                return;
              }
              setPaymentModal(true);
            }}
            disabled={state.items.length === 0}
            className="mt-4 w-full rounded-xl bg-gradient-to-r from-primary to-secondary py-5 text-lg font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-[0_0_20px_hsl(160_84%_39%/0.4)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-none"
          >
            FINALIZAR VENDA (F2)
          </button>

          {/* Info */}
          <div className="mt-4 space-y-3 rounded-xl border border-border/50 bg-card p-4">
            <div className="flex items-center justify-between">
              <span className="text-base text-muted-foreground">Itens</span>
              <span className="text-base font-bold text-foreground">{state.itemCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-base text-muted-foreground">Cliente</span>
              </div>
              <span className="text-base font-medium text-primary">
                {state.customer?.name || '-'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-base text-muted-foreground">Pontos</span>
              <span className="text-base font-medium text-foreground">
                {customerPoints !== null ? customerPoints : '-'}
              </span>
            </div>
          </div>

          {/* Spacer */}
          <div className="flex-1" />
        </div>
      </div>

      {/* ACTION BUTTONS BAR */}
      <footer className="border-t border-border/50 bg-card/80 px-4 py-2 backdrop-blur-[10px]">
        <div className="flex items-center gap-2 overflow-x-auto">
          {/* Finalizar Venda - Green, prominent */}
          <button
            onClick={() => {
              if (state.items.length === 0) {
                toast.warning('Adicione itens antes de finalizar');
                return;
              }
              setPaymentModal(true);
            }}
            className="flex min-h-[44px] items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-all hover:bg-emerald-500 hover:shadow-lg active:scale-95"
          >
            <CheckCircle className="h-4 w-4" />
            <span>Finalizar Venda</span>
            <kbd className="ml-1 rounded bg-emerald-700/60 px-1.5 py-0.5 font-mono text-[10px]">F2</kbd>
          </button>

          {/* Pagamento */}
          <button
            onClick={() => {
              if (state.items.length === 0) {
                toast.warning('Adicione itens antes de finalizar');
                return;
              }
              setPaymentModal(true);
            }}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-all hover:bg-muted hover:border-primary/30 active:scale-95"
          >
            <CreditCard className="h-4 w-4 text-blue-400" />
            <span>Pagamento</span>
            <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">F3</kbd>
          </button>

          {/* Desconto */}
          <button
            onClick={() => {
              if (state.items.length === 0) {
                toast.warning('Adicione itens antes de aplicar desconto');
                return;
              }
              setDiscountModal(true);
            }}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-all hover:bg-muted hover:border-primary/30 active:scale-95"
          >
            <Percent className="h-4 w-4 text-amber-400" />
            <span>Desconto</span>
            <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">F6</kbd>
          </button>

          {/* Cancelar Item */}
          <button
            onClick={() => {
              if (state.items.length > 0) {
                const lastItem = state.items[state.items.length - 1]!;
                removeItem(lastItem.id);
                toast.info(`${lastItem.product_name} removido`);
              }
            }}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-all hover:bg-muted hover:border-destructive/30 active:scale-95"
          >
            <XCircle className="h-4 w-4 text-orange-400" />
            <span>Cancelar Item</span>
            <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">F4</kbd>
          </button>

          {/* Cancelar Venda */}
          <button
            onClick={() => {
              if (state.items.length > 0) {
                setCancelModal(true);
              } else {
                toast.info('Nenhuma venda em andamento');
              }
            }}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-all hover:bg-destructive/10 hover:border-destructive/30 hover:text-destructive active:scale-95"
          >
            <Ban className="h-4 w-4 text-red-400" />
            <span>Cancelar Venda</span>
            <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">F5</kbd>
          </button>

          {/* Sangria/Suprimento */}
          <button
            onClick={() => setSangriaModal(true)}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-all hover:bg-muted hover:border-primary/30 active:scale-95"
          >
            <ArrowDownUp className="h-4 w-4 text-purple-400" />
            <span>Sangria</span>
            <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">F7</kbd>
          </button>

          {/* Cliente */}
          <button
            onClick={() => setCustomerSearchModal(true)}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-all hover:bg-muted hover:border-primary/30 active:scale-95"
          >
            <UserCheck className="h-4 w-4 text-cyan-400" />
            <span>Cliente</span>
            <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">F8</kbd>
          </button>

          {/* Fechar Caixa */}
          <button
            onClick={() => {
              if (state.items.length > 0) {
                toast.warning('Finalize ou cancele a venda antes de fechar o caixa');
                return;
              }
              setCashRegisterModal({ open: true, mode: 'close' });
            }}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm font-medium text-foreground transition-all hover:bg-muted hover:border-destructive/30 active:scale-95"
          >
            <LockKeyhole className="h-4 w-4 text-red-400" />
            <span>Fechar Caixa</span>
          </button>
        </div>
      </footer>

      {/* Modals */}
      <QuickProductModal
        isOpen={quickProductModal.open}
        barcode={quickProductModal.barcode}
        onSave={handleQuickProductSave}
        onCancel={() => setQuickProductModal({ open: false, barcode: '' })}
      />

      <WeightInputModal
        isOpen={weightModal.open}
        productName={weightModal.productName}
        unitPrice={weightModal.unitPrice}
        unit={weightModal.unit}
        scaleAvailable={false}
        onConfirm={handleWeightConfirm}
        onCancel={() => setWeightModal({ open: false, productName: '', unitPrice: 0, unit: 'kg', productId: '' })}
      />

      <PaymentModal
        isOpen={paymentModal}
        saleId={state.saleId}
        saleTotal={state.total}
        onClose={() => setPaymentModal(false)}
        onSuccess={(data) => {
          setPaymentModal(false);
          clearSale();
          setCustomer(null);
          setCustomerPoints(null);
          setLastReceipt(localStorage.getItem('nexopdv_last_receipt'));
          beepPayment();
          toast.success(`Venda finalizada! Cupom: ${data.receipt_number}`);
        }}
      />

      <DiscountModal
        isOpen={discountModal}
        saleId={state.saleId}
        saleSubtotal={state.subtotal}
        onClose={() => setDiscountModal(false)}
        onSuccess={({ discount_amount }) => {
          setDiscount(discount_amount);
        }}
      />

      <CancelSaleModal
        isOpen={cancelModal}
        saleId={state.saleId}
        saleStatus="em_andamento"
        onClose={() => setCancelModal(false)}
        onSuccess={() => {
          clearSale();
        }}
      />

      <RefundModal
        isOpen={refundModal}
        onClose={() => setRefundModal(false)}
        onSuccess={() => {
          // Refund processed
        }}
      />

      <CashRegisterModal
        isOpen={cashRegisterModal.open}
        mode={cashRegisterModal.mode}
        onClose={() => setCashRegisterModal({ open: false, mode: 'open' })}
        onSuccess={() => {
          if (cashRegisterModal.mode === 'open') {
            setRegisterOpen(true);
          } else {
            setRegisterOpen(false);
          }
        }}
      />

      <SangriaModal
        isOpen={sangriaModal}
        onClose={() => setSangriaModal(false)}
        onSuccess={() => {
          // Movement registered
        }}
      />

      <CustomerSearchModal
        isOpen={customerSearchModal}
        onClose={() => setCustomerSearchModal(false)}
        onSelect={(customer) => {
          setCustomer({ id: customer.id, name: customer.full_name });
          setCustomerPoints(null);
          // Fetch loyalty points for selected customer
          apiFetch(`/api/loyalty/customer/${customer.id}`)
            .then((res) => res.ok ? res.json() : null)
            .then((data) => {
              if (data) setCustomerPoints(data.pontos_disponiveis ?? 0);
            })
            .catch(() => {});
          toast.success(`Cliente: ${customer.full_name}`);
          setCustomerSearchModal(false);
        }}
      />

      {/* Logout Confirmation Modal */}
      {logoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <LogOut className="h-6 w-6 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Sair do sistema?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Você será desconectado e redirecionado para a tela de login.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setLogoutConfirm(false)}
                className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 rounded-xl bg-destructive px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
